// src/edu-world/systems/farewell-cues.ts
// Farewell Cue System — winding-down suggestions and positive farewell moments
// Standalone module (Phase 3) — pure logic, Phaser-agnostic
// NEVER forces termination — suggestions only
// Requirements: 22.4, 22.5, 22.6, 22.7

// ─── Types ───

export interface SessionStateInput {
  missionsCompleted: number;
  elapsedMs: number;
  phase: 'active' | 'winding_down' | 'farewell';
}

export type WindingDownCueType =
  | 'npc_tiredness'
  | 'gentle_dimming'
  | 'farewell_character';

export interface WindingDownSuggestion {
  /** Type of winding-down cue to present */
  cueType: WindingDownCueType;
  /** Dialogue text for NPC tiredness (max 8 words, pt-PT) */
  dialogue?: string;
  /** Animation reference to play */
  animationRef: string;
  /** Whether this is a suggestion only (always true — never forced) */
  isSuggestionOnly: true;
}

export type FarewellAnimationType = 'sunset' | 'sleepy_npcs' | 'stars';

export interface FarewellConfig {
  /** Farewell animation type */
  animationType: FarewellAnimationType;
  /** Audio reference for "Até amanhã!" message */
  audioRef: string;
  /** NPC name for farewell character */
  farewellNpcName: string;
  /** Farewell dialogue text (max 8 words, pt-PT) */
  farewellDialogue: string;
  /** Summary of world changes made during session */
  worldChangesSummary: string[];
  /** Maximum duration of farewell moment in milliseconds */
  maxDurationMs: number;
  /** Wave animation reference */
  waveAnimationRef: string;
}

export interface FarewellCueConfig {
  /** Missions threshold for winding-down suggestions (default: 2) */
  windingDownMissionThreshold: number;
  /** Missions threshold for farewell moment (default: 3) */
  farewellMissionThreshold: number;
  /** Time threshold for farewell moment in ms (default: 12 minutes) */
  farewellTimeThresholdMs: number;
  /** Max duration for game-close farewell in ms (default: 3000) */
  gameCloseFarewellMaxMs: number;
}

// ─── Default Configuration ───

const DEFAULT_CONFIG: FarewellCueConfig = {
  windingDownMissionThreshold: 2,
  farewellMissionThreshold: 3,
  farewellTimeThresholdMs: 12 * 60 * 1000, // 12 minutes
  gameCloseFarewellMaxMs: 3000,
};

// ─── Winding-Down Cue Pools ───

const WINDING_DOWN_CUES: WindingDownSuggestion[] = [
  {
    cueType: 'npc_tiredness',
    dialogue: 'Estou cansado, vamos descansar?',
    animationRef: 'npc-yawn-stretch',
    isSuggestionOnly: true,
  },
  {
    cueType: 'gentle_dimming',
    animationRef: 'world-gentle-dim',
    isSuggestionOnly: true,
  },
  {
    cueType: 'farewell_character',
    dialogue: 'Até logo, amiguinho!',
    animationRef: 'farewell-npc-appear',
    isSuggestionOnly: true,
  },
];

// ─── FarewellCueSystem ───

/**
 * Manages winding-down suggestions and farewell moments for session pacing.
 *
 * Key rules:
 * - At ≥2 missions: suggest winding-down (NPC tiredness, gentle dimming, farewell character)
 * - At ≥3 missions OR >12 minutes: positive stopping moment
 * - NEVER force termination — all suggestions are optional
 * - On game close: brief farewell (max 3 seconds)
 */
export class FarewellCueSystem {
  private config: FarewellCueConfig;
  private cueIndex: number = 0;
  private lastCueSuggested: number = 0;
  private sessionWorldChanges: string[] = [];

  constructor(config?: Partial<FarewellCueConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Check if a winding-down suggestion should be shown based on session state.
   * Returns a suggestion config if appropriate, or null if not yet time.
   *
   * At ≥2 missions completed: returns winding-down cues (NPC tiredness, dimming, farewell character).
   * These are SUGGESTIONS ONLY — the child may continue playing freely.
   */
  checkWindingDown(sessState: SessionStateInput): WindingDownSuggestion | null {
    // Only suggest winding-down when threshold is met
    if (sessState.missionsCompleted < this.config.windingDownMissionThreshold) {
      return null;
    }

    // Don't suggest again within 30 seconds of last suggestion
    const now = Date.now();
    if (now - this.lastCueSuggested < 30000) {
      return null;
    }

    // If already in farewell phase, don't show winding-down (show farewell instead)
    if (this.shouldShowFarewell(sessState)) {
      return null;
    }

    // Cycle through different cue types for variety
    const cue = WINDING_DOWN_CUES[this.cueIndex % WINDING_DOWN_CUES.length];
    this.cueIndex++;
    this.lastCueSuggested = now;

    return { ...cue };
  }

  /**
   * Determine if the farewell moment should be presented.
   * True when: ≥3 missions completed OR session exceeds 12 minutes.
   * This is still a SUGGESTION — never forced.
   */
  shouldShowFarewell(sessState: SessionStateInput): boolean {
    const missionThresholdMet =
      sessState.missionsCompleted >= this.config.farewellMissionThreshold;
    const timeThresholdMet =
      sessState.elapsedMs >= this.config.farewellTimeThresholdMs;

    return missionThresholdMet || timeThresholdMet;
  }

  /**
   * Create the farewell moment configuration.
   * Used when the child is closing the game or when the farewell phase is triggered.
   *
   * Includes: farewell animation, "Até amanhã!" audio reference,
   * world changes summary, and a warm goodbye wave (max 3 seconds).
   */
  createFarewellMoment(): FarewellConfig {
    return {
      animationType: 'sleepy_npcs',
      audioRef: 'audio/farewell/ate-amanha.mp3',
      farewellNpcName: 'Rosa',
      farewellDialogue: 'Até amanhã, amiguinho!',
      worldChangesSummary: [...this.sessionWorldChanges],
      maxDurationMs: this.config.gameCloseFarewellMaxMs,
      waveAnimationRef: 'player-wave-goodbye',
    };
  }

  /**
   * Record a world change that happened during this session.
   * These are summarized in the farewell moment.
   */
  recordWorldChange(description: string): void {
    this.sessionWorldChanges.push(description);
  }

  /**
   * Reset session tracking (call on new session start).
   */
  resetSession(): void {
    this.cueIndex = 0;
    this.lastCueSuggested = 0;
    this.sessionWorldChanges = [];
  }

  /**
   * Get the world changes recorded this session.
   */
  getSessionWorldChanges(): string[] {
    return [...this.sessionWorldChanges];
  }
}
